'use client'

import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { 
  ShoppingBag, 
  Minus, 
  Plus, 
  Trash2, 
  Send, 
  Loader2,
  CheckCircle2,
  User,
  Mail,
  Phone,
  MessageSquare,
  Building2
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useToast } from '@/components/ui/use-toast'
import { useInquiryStore } from '@/lib/stores/inquiry-store'
import { siloApi } from '@/lib/api/silo-api'

// Form validation schema
const contactFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, 'Name must be at least 2 characters'),
  businessName: z
    .string()
    .trim()
    .min(2, 'Business name is required and must be at least 2 characters'), // Made required
  email: z
    .string()
    .trim()
    .email('Please enter a valid email address'),
  phone: z
    .string()
    .trim()
    .min(1, 'Phone number is required')
    .transform(v => v.replace(/\s+/g, ''))
    .refine(v => /^(\+)?[0-9()\-]*$/.test(v), {
      message: 'Phone can include digits, +, (), -',
    })
    .transform(v => v.replace(/[^0-9]/g, ''))
    .refine(v => v.length >= 10 && v.length <= 15, {
      message: 'Phone must be 10-15 digits',
    }),
  comments: z.string().trim().optional(),
})

type ContactFormData = z.infer<typeof contactFormSchema>

interface ContactFormSectionProps {
  onSubmitSuccess?: () => void
}

export default function ContactFormSection({ onSubmitSuccess }: ContactFormSectionProps) {
  const { toast } = useToast()
  const { selectedItems, updateQuantity, removeItem, clearItems } = useInquiryStore()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      name: '',
      businessName: '',
      email: '',
      phone: '',
      comments: '',
    },
  })

  // Format quantity display
  const formatQuantityDisplay = (quantity: number) => {
    if (quantity >= 1000 && quantity % 1000 === 0) {
      return `${quantity / 1000}kg`
    }
    return `${quantity}g`
  }

  // Handle quantity updates
  const handleUpdateQuantity = (productId: number, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeItem(productId)
    } else {
      updateQuantity(productId, newQuantity)
    }
  }

  // Submit form
  const onSubmit = async (data: ContactFormData) => {
    if (selectedItems.length === 0) {
      toast({
        title: 'No Products Selected',
        description: 'Please select at least one product before submitting your sample request.',
        variant: 'destructive',
      })
      return
    }

    setIsSubmitting(true)

    try {
      // Prepare selected products with basic info
      const selectedProductsBasic = selectedItems.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        quantity: item.quantity,
        unit: item.unit,
      }))

      // Prepare full product details for metadata
      const productDetails = selectedItems.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        quantity: item.quantity,
        unit: item.unit,
        price_per_unit: item.price_per_unit,
        // Include full product data if available
        ...(item.product_data && {
          sku: item.product_data.sku,
          product_type: item.product_data.product_type,
          cannabinoids: item.product_data.cannabinoids,
          terpenes: item.product_data.terpenes,
          description: item.product_data.description,
          image_url: item.product_data.image_url,
        })
      }))

      // Prepare the submission data with metadata
      const submissionData = {
        name: data.name,
        email: data.email,
        phone: data.phone,
        company: data.businessName, // CRITICAL: Send company at root level for business creation
        comments: data.comments || '',
        selected_products: selectedProductsBasic,
        metadata: {
          business_name: data.businessName, // Keep in metadata for backward compatibility
          product_details: productDetails,
          submission_timestamp: new Date().toISOString(),
          source: 'inventory_browser',
        }
      }

      console.log('Submitting contact inquiry with full metadata:', submissionData)

      // Submit to API
      const response = await siloApi.submitPublicContactForm(submissionData)
      
      console.log('Contact inquiry response:', response)

      setSubmitSuccess(true)
      clearItems()
      form.reset()

      toast({
        title: 'Sample Request Submitted Successfully!',
        description: 'We\'ve received your sample request and will contact you soon with shipping details.',
      })

      // Call success callback
      onSubmitSuccess?.()

    } catch (error: any) {
      console.error('Failed to submit inquiry:', error)
      
      toast({
        title: 'Submission Failed',
        description: error.message || 'Failed to submit your sample request. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Success screen
  if (submitSuccess) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center space-y-4 p-6">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, type: "spring" }}
          >
            <CheckCircle2 className="h-16 w-16 text-green-400 mx-auto" />
          </motion.div>
          <div>
            <h3 className="text-xl font-semibold text-hierarchy-1 mb-2">
              Sample Request Submitted!
            </h3>
            <p className="text-hierarchy-3">
              Thank you for your interest. We'll contact you within 24 hours with product availability and pricing information.
            </p>
          </div>
          <Button 
            onClick={() => setSubmitSuccess(false)}
            className="bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 text-black font-semibold"
          >
            Submit Another Sample Request
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex-1 scroll-safe pr-2">
      {/* Selected Products Section */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <ShoppingBag className="h-5 w-5 text-yellow-400" />
          <h3 className="text-lg font-semibold text-hierarchy-1">
            Sample Request ({selectedItems.length} product{selectedItems.length !== 1 ? 's' : ''})
          </h3>
        </div>

        {selectedItems.length === 0 ? (
          <Card className="glass-card p-4">
            <p className="text-center text-white/60 py-2">
              No products selected. Browse products on the left to add items to your sample request.
            </p>
          </Card>
        ) : (
          <Card className="glass-card p-4 max-h-48 overflow-y-auto">
            <div className="space-y-3">
              {selectedItems.map((item, index) => (
                <div key={`product-${item.product_id}-${index}`} className="flex items-center justify-between py-2 border-b border-white/5 last:border-b-0">
                  <div className="flex-1 min-w-0">
                    <p className="text-hierarchy-1 font-medium text-sm truncate">
                      {item.product_name}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => removeItem(item.product_id)}
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-red-400 hover:bg-red-400/10"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* Contact Form Section */}
      <div>
        <h3 className="text-lg font-semibold text-hierarchy-1 mb-3 flex items-center gap-2">
          <Mail className="h-5 w-5 text-yellow-400" />
          Contact Information
        </h3>

        <Form {...form}>
          <form id="sampleRequestForm" onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-white/80 flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Full Name
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Enter your full name"
                      className="bg-gray-800/30 border-white/10 text-white placeholder:text-white/40"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="businessName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-white/80 flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Business Name *
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Enter your business name"
                      required
                      className="bg-gray-800/30 border-white/10 text-white placeholder:text-white/40"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-white/80 flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email Address
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      placeholder="Enter your email address"
                      className="bg-gray-800/30 border-white/10 text-white placeholder:text-white/40"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-white/80 flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Phone Number *
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="tel"
                      placeholder="Enter your phone number"
                      required
                      className="bg-gray-800/30 border-white/10 text-white placeholder:text-white/40"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="comments"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-white/80 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Additional Comments (Optional)
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Tell us about your intended use case or specific product requirements for these samples..."
                      className="bg-gray-800/30 border-white/10 text-white placeholder:text-white/40 min-h-[80px] resize-none"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Submit button moved to footer below scroll area */}
          </form>
        </Form>
      </div>
      {/* End Contact Form Section */}
      </div>
      {/* End Scroll Area */}
      {/* Footer: anchored button, outside scroll area */}
      <div className="shrink-0 pt-4">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                form="sampleRequestForm"
                type="submit"
                disabled={isSubmitting || !form.formState.isValid || selectedItems.length === 0}
                className={`
                  w-full h-12 rounded-full font-semibold transition-all duration-300
                  ${form.formState.isValid && selectedItems.length > 0 && !isSubmitting
                    ? 'bg-blue-500/20 border-2 border-blue-400/50 text-blue-100 backdrop-blur-sm shadow-lg shadow-blue-500/25 hover:bg-blue-500/30 hover:shadow-blue-500/40 hover:border-blue-400/70'
                    : 'bg-white/5 border border-white/10 text-white/40 backdrop-blur-sm cursor-not-allowed'
                  }
                `}
              >
                {isSubmitting ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Submitting Sample Request...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Send className="h-4 w-4" />
                    Request Samples
                  </div>
                )}
              </Button>
            </TooltipTrigger>
            {(!form.formState.isValid || selectedItems.length === 0) && (
              <TooltipContent className="bg-gray-900 border-gray-700 text-white max-w-xs">
                <div className="space-y-1 text-sm">
                  <p className="font-semibold text-yellow-400">Please complete the following:</p>
                  {selectedItems.length === 0 && (
                    <p className="flex items-center gap-1">
                      <span className="text-red-400">•</span> Select at least one product
                    </p>
                  )}
                  {form.formState.errors.name && (
                    <p className="flex items-center gap-1">
                      <span className="text-red-400">•</span> Enter your full name
                    </p>
                  )}
                  {form.formState.errors.businessName && (
                    <p className="flex items-center gap-1">
                      <span className="text-red-400">•</span> Enter your business name
                    </p>
                  )}
                  {form.formState.errors.email && (
                    <p className="flex items-center gap-1">
                      <span className="text-red-400">•</span> Enter a valid email address
                    </p>
                  )}
                  {form.formState.errors.phone && (
                    <p className="flex items-center gap-1">
                      <span className="text-red-400">•</span> Enter a valid phone number
                    </p>
                  )}
                </div>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  )
}